import Timer from 'app/components/reusable/Timer';
import GameInterface from 'app/interfaces/GameInterface';
import MultiLangsInterface from 'app/interfaces/MultiLangsInterface';
import CompendiumAnswerInterface from 'app/interfaces/CompendiumAnswerInterface';
import {ELEMENT_NOT_FOUND} from 'app/utils/Consts';
import React from 'react';
import I18n from 'app/i18n';
import {isGuest, localePromoName, promoUrl, serverError, timeStampToISODateFormat} from 'app/utils';
import {modalConfirm} from 'app/utils/modals/popups';
import {showPopup} from 'app/utils/modals/ModalsService';
import Answer from './predictions/answer';
import CancelPrediction from 'app/components/compendium/CancelPrediction';
import NotEnoughPredictionPointsPopup from 'app/popups/NotEnoughPredictionPointsPopup';
import {openAuth} from 'app/components/auth/auth';
import {toastError, toastSuccess} from 'app/components/toasts/liteToast';
import {langsValue} from 'app/utils/langs/langsValue';
import {isCanceledByUser, isCanceledByAdmin, isCalculated, isActive} from 'app/components/compendium/predictions/predictionState';
import RenderState from 'app/components/compendium/predictions/renderState';
import RenderUserAnswer from 'app/components/compendium/predictions/renderUserAnswer';
import RenderStatusIcon from 'app/components/compendium/predictions/renderStatusIcon';
import Post from 'app/utils/Post';
import SpriteIcon from 'app/utils/sprites/SpriteIcon';

interface Props {
    answerWasGiven: boolean
    answers: CompendiumAnswerInterface[]
    correctAnswers: number[]
    correct_answers_count: number
    due_date: number
    game: GameInterface
    id: number
    langs: MultiLangsInterface
    question: string
    reward: boolean
    selected: boolean
    showAnswersHandler: (predictionId?: number) => void
    submitAnswer: (predictionId: number) => void
    user_answers: number[],
    day: number
    isMyPredictionPage?: boolean
    date?: number
    state?: string
    rating_points?: number
    correct_answers?: CompendiumAnswerInterface[]
    answer?: CompendiumAnswerInterface
    cancelAnswer?: (predictionId: number) => void
}

interface States {
    answer_id?: number,
    disabled: boolean,
    multi_answers?: number[],
    showAnswers: boolean,
    submitting: boolean,
}

export default class Prediction extends React.PureComponent<Props, States> {
    constructor(props: Props) {
        super(props);
        this.state = {
            answer_id: null,
            disabled: true,
            multi_answers: [],
            showAnswers: false,
            submitting: false
        };
        this.submitDisabled = this.submitDisabled.bind(this);
        this.selectAnswer = this.selectAnswer.bind(this);
        this.selectMultiAnswer = this.selectMultiAnswer.bind(this);
        this.submitAnswer = this.submitAnswer.bind(this);
        this.resetSubmitting = this.resetSubmitting.bind(this);
        this.showAnswersHandler = this.showAnswersHandler.bind(this);
    }

    componentDidMount(): void {
        const {user_answers, correct_answers_count} = this.props;

        if (user_answers.length <= 0) {
            return;
        }

        const newState: {multi_answers?: number[], answer_id?: number, disabled: boolean} = {disabled: true};

        if (correct_answers_count > 1) {
            newState.multi_answers = user_answers;
        } else {
            const [answer_id] = user_answers;
            newState.answer_id = answer_id;
        }

        this.setState(newState);
    }

    componentDidUpdate(prevProps: Props): void {
        if (this.props.selected !== prevProps.selected) {
            this.setState({showAnswers: this.props.selected});
        }
    }

    componentWillUnmount(): void {
        if (this.state.showAnswers) {
            this.props.showAnswersHandler();
        }
    }

    isSelectedAnswer(answer: CompendiumAnswerInterface): boolean {
        const {multi_answers, answer_id} = this.state;

        if (multi_answers.length > 0) {
            return multi_answers.indexOf(answer.id) !== ELEMENT_NOT_FOUND;
        }

        return answer_id && answer_id === answer.id;
    }

    selectAnswer(answer_id: number): void {
        this.setState({
            answer_id,
            disabled: false
        });
    }

    selectMultiAnswer(answer: {checked: boolean, id: number}): void {
        let res = this.state.multi_answers.slice();
        if (answer.checked) {
            res.push(answer.id);
        } else {
            res = res.filter(a => a !== answer.id);
        }
        this.setState({
            disabled: res.length !== this.props.correct_answers_count,
            multi_answers: res
        });
    }

    resetSubmitting(): void {
        this.setState({submitting: false});
    }

    submitAnswer(): void {
        if (isGuest()) {
            return openAuth();
        }

        this.setState({submitting: true});

        modalConfirm(I18n.t('compendium.are_you_sure_prediction'), () => new Post({
            params: {
                answer_id: this.props.correct_answers_count === 1 ? this.state.answer_id : this.state.multi_answers,
                prediction_id: this.props.id
            },
            url: promoUrl('answer')
        }).execute()
            .then(response => {
                if (response.ok) {
                    response.json().then(data => {
                        this.resetSubmitting();
                        if (data.success) {
                            this.props.submitAnswer(this.props.id);
                            toastSuccess(I18n.t('compendium.successful_prediction'));
                            if (this.state.showAnswers) {
                                this.showAnswersHandler();
                            }
                        } else {
                            if (data.error === I18n.t(`${localePromoName()}.not_enough_prediction_points`)) {
                                return showPopup(
                                    'not-enough-predictions-points-popup',
                                    (props: {popupId: string}) => <NotEnoughPredictionPointsPopup popupId={props.popupId}/>
                                );
                            }

                            toastError(data.error);
                        }
                        return null;
                    });
                } else {
                    serverError(this.resetSubmitting);
                }
            }), this.resetSubmitting);
        return null;
    }

    submitDisabled(): boolean {
        return this.state.submitting || this.state.disabled;
    }

    gridClass(): string {
        const classes = ['compendium-grid__item'];

        if (this.state.showAnswers) {
            classes.push('compendium-grid__item--active');
        }
        return classes.join(' ');
    }

    showAnswersHandler = (): void => {
        this.props.showAnswersHandler(this.props.id);
    };

    handleShowAnswersHandler = (e: React.MouseEvent<HTMLAnchorElement>): void => {
        e.preventDefault();
        this.showAnswersHandler();
    };

    doesHaveResult(): boolean {
        return this.props.correctAnswers.length > 0;
    }

    makePredictionButtonText(): React.ReactNode {
        return <SpriteIcon name="#svg-arrow-for-menu" className="svg-default"/>;
    }

    cancelAnswer = (): void => {
        const {id, cancelAnswer: propsCancelAnswer} = this.props;

        new CancelPrediction(id, () => {
            this.setState({
                answer_id: null,
                multi_answers: []
            });
            propsCancelAnswer && propsCancelAnswer(id);
        }).cancelAnswer();
    };

    get isCorrectAnswer(): boolean {
        const {rating_points} = this.props;
        return rating_points > 0;
    }

    gameClass = (gameName: string | undefined): string => {
        switch (gameName) {
        case 'CS':
        case 'CS:GO':
            return 'csgo';
        case 'Dota 2':
            return 'dota2';
        case 'Lol':
        case 'LoL':
            return 'lol';
        default:
            return '';
        }
    };

    render(): React.ReactNode {
        const {answerWasGiven, user_answers, game, due_date, date, day, isMyPredictionPage, state} = this.props;
        const isCalculatedState = isCalculated(state);
        const stateItemClass = isCalculatedState && (this.isCorrectAnswer ? 'win' : 'lose') || '';
        const gameItemClass = this.gameClass(game?.name);
        const showButtonCancel = isMyPredictionPage ? !isCalculatedState && isActive(state) : answerWasGiven;
        const renderRP = isCalculatedState && this.isCorrectAnswer ? this.props.rating_points : this.props.reward;

        return <div className={this.gridClass()}>
            <div className={`compendium-grid__item-inner${answerWasGiven ? ' answer-given' : ''} ${gameItemClass} ${stateItemClass}`}>
                <div className="compendium-grid__item-header">
                    <SpriteIcon name="#svg-cup-table" className="svg-default"/>
                    <span>{renderRP}</span>
                    <span>RP</span>
                </div>
                <div className="compendium-grid__item-timer">
                    {game?.filter_image?.url && <span className="compendium-grid__item-timer__logo">
                        <img
                            src={game.filter_image.url}
                            alt={game.name}
                            title={game.name} />
                    </span>}
                    <span className="compendium-grid__item-timer__time">
                        {isMyPredictionPage ? timeStampToISODateFormat(date) : <Timer endTime={due_date}/>}
                    </span>
                </div>
                <div className="compendium-grid__item-question">
                    {langsValue(this.props, 'question')} {!isMyPredictionPage && `(${I18n.t('day')} ${day})`}
                </div>
                {isMyPredictionPage
                    ? <RenderStatusIcon isCalculated={isCalculatedState} isCorrectAnswer={this.isCorrectAnswer} />
                    : <a
                        href="#"
                        onClick={this.handleShowAnswersHandler}
                        className="compendium-open-question"
                    >
                        {this.makePredictionButtonText()}
                    </a>}
                {showButtonCancel && <button onClick={this.cancelAnswer} className="button-adaptive button-adaptive_casino-icon">
                    {I18n.t('button_cancel')}
                </button>}
                <RenderState cancelByUser={isCanceledByUser(state)} cancelByAdmin={isCanceledByAdmin(state)} />
                {this.state.showAnswers && <div
                    className={`compendium-grid__addon compendium-grid__addon--shown${answerWasGiven
                        ? ' compendium-grid__addon--answered'
                        : ''} `}
                >
                    <div className="compendium-grid__addon-background">
                        <div className="compendium-grid__addon-options">
                            {this.props.answers.map(answer => <Answer
                                key={answer.id}
                                prediction_id={this.props.id}
                                correct_answers_count={this.props.correct_answers_count}
                                selectAnswer={this.selectAnswer}
                                isSelectedAnswer={this.isSelectedAnswer(answer)}
                                selectMultiAnswer={this.selectMultiAnswer}
                                answerWasGiven={answerWasGiven}
                                user_answers={user_answers}
                                {...answer}
                            />)}
                        </div>
                        {!answerWasGiven && <div className="compendium-grid__addon-action">
                            <button
                                className="button-adaptive button-adaptive_uppercase"
                                disabled={this.submitDisabled()}
                                onClick={this.submitAnswer}
                            >
                                {I18n.t(`user.promo_action.${localePromoName()}.make_prediction`)}
                            </button>
                        </div>}
                    </div>
                </div>}
                <RenderUserAnswer
                    isMyPredictionPage={isMyPredictionPage}
                    isCorrectAnswer={this.isCorrectAnswer}
                    isCalculated={isCalculatedState}
                    answer={this.props.answer}
                    correct_answers={this.props.correct_answers}
                />
            </div>
        </div>;
    }
}
